import { useState } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'sonner';

export const useExport = () => {
  const [exporting, setExporting] = useState(false);

  const exportToExcel = (data, filename, sheetName = 'Dados') => {
    try {
      setExporting(true);
      
      // Create a new workbook
      const wb = XLSX.utils.book_new();
      
      // Convert data to worksheet
      const ws = XLSX.utils.json_to_sheet(data);
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      
      // Generate Excel file and trigger download
      XLSX.writeFile(wb, `${filename}.xlsx`);
      
      toast.success('Arquivo Excel exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      toast.error('Erro ao exportar arquivo Excel');
    } finally {
      setExporting(false);
    }
  };

  const exportToPDF = (data, filename, title = 'Relatório', columns = [], summaryData = null) => {
    try {
      setExporting(true);
      
      if (!data || data.length === 0) {
        toast.error('Nenhum dado para exportar');
        return;
      }
      
      // Create new PDF document in landscape mode for better table viewing
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      // Header
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('GridProfile', 14, 20);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text('Sistema de Folha de Pagamento', 14, 28);
      
      // Report title
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(title, 14, 40);
      
      // Generated date
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`, 14, 50);
      
      // Prepare table data
      let tableColumns = columns;
      let tableRows = [];
      
      if (columns.length === 0 && data.length > 0) {
        const keys = Object.keys(data[0]).slice(0, 8);
        tableColumns = keys.map(key => ({
          header: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
          dataKey: key
        }));
      }
      
      // Convert data to table rows
      tableRows = data.map(row => {
        return tableColumns.map(col => {
          let value = row[col.dataKey];
          
          if (value === undefined || value === null) {
            return '';
          }
          
          if (typeof value === 'number' && (
            col.dataKey.includes('total') || 
            col.dataKey.includes('salario') ||
            col.dataKey.includes('vt') ||
            col.dataKey.includes('va') ||
            col.dataKey.includes('vr') ||
            col.dataKey.includes('fgts') ||
            col.dataKey.includes('inss') ||
            col.dataKey.includes('prov') ||
            col.dataKey.includes('nf_13') ||
            col.dataKey.includes('acumulo') ||
            col.dataKey.includes('desconto') ||
            col.dataKey.includes('custo') ||
            col.dataKey.includes('aux') ||
            col.dataKey.includes('assist') ||
            col.dataKey.includes('odonto') ||
            col.dataKey.includes('seg') ||
            col.dataKey.includes('pass') ||
            col.dataKey.includes('med') ||
            col.dataKey.includes('segunda') ||
            col.dataKey.includes('pernoite') ||
            col.dataKey.includes('creche')
          )) {
            try {
              return new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
                minimumFractionDigits: 2
              }).format(value);
            } catch (e) {
              return String(value);
            }
          }
          
          if (col.dataKey.includes('admissao') && value) {
            try {
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                return date.toLocaleDateString('pt-BR');
              }
            } catch (e) {
            }
          }
          
          return String(value || '');
        });
      });
      
      // Add main table
      let finalY = 60;
      autoTable(doc, {
        startY: finalY,
        head: [tableColumns.map(col => col.header)],
        body: tableRows,
        styles: {
          fontSize: 7,
          cellPadding: 1.5,
          overflow: 'linebreak',
          halign: 'center',
        },
        headStyles: {
          fillColor: [59, 130, 246],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 8,
          halign: 'center',
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252],
        },
        margin: { left: 10, right: 10 },
        tableWidth: 'auto',
        theme: 'striped',
      });
      
      finalY = doc.lastAutoTable.finalY + 10;
      
      // Add summary section if provided
      if (summaryData) {
        const pageWidth = doc.internal.pageSize.getWidth();
        
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Resumo', 14, finalY);
        
        finalY += 8;
        
        const summaryRows = summaryData.map(item => [
          item.label,
          item.valor
        ]);
        
        autoTable(doc, {
          startY: finalY,
          head: [['Indicador', 'Valor']],
          body: summaryRows,
          styles: {
            fontSize: 10,
            cellPadding: 3,
            halign: 'left',
          },
          headStyles: {
            fillColor: [22, 163, 74],
            textColor: 255,
            fontStyle: 'bold',
            fontSize: 10,
          },
          columnStyles: {
            0: { fontStyle: 'bold', cellWidth: 'auto' },
            1: { halign: 'right', cellWidth: 80 },
          },
          margin: { left: 14, right: 14 },
          tableWidth: 'auto',
          theme: 'grid',
        });
      }
      
      // Save PDF
      doc.save(`${filename}.pdf`);
      
      toast.success('Arquivo PDF exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      toast.error(`Erro ao exportar PDF: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setExporting(false);
    }
  };

  const exportToExcelMultiSheet = (sheets, filename) => {
    try {
      setExporting(true);
      const wb = XLSX.utils.book_new();
      sheets.forEach(({ name, data }) => {
        const ws = XLSX.utils.json_to_sheet(data);
        XLSX.utils.book_append_sheet(wb, ws, name);
      });
      XLSX.writeFile(wb, `${filename}.xlsx`);
      toast.success('Arquivo Excel exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      toast.error('Erro ao exportar arquivo Excel');
    } finally {
      setExporting(false);
    }
  };

  const exportToCSV = (data, filename, headers = []) => {
    try {
      setExporting(true);
      
      let csvContent = '';
      
      // Add company header
      csvContent += 'GridProfile - Gestão Inteligente de RH\n';
      csvContent += `Relatório gerado em: ${new Date().toLocaleString('pt-BR')}\n`;
      csvContent += '\n';
      
      if (data.length === 0) {
        csvContent += 'Nenhum dado disponível\n';
      } else {
        // Add headers
        if (headers.length > 0) {
          csvContent += headers.join(',') + '\n';
        } else {
          // Auto-generate headers from data keys
          const keys = Object.keys(data[0]);
          csvContent += keys.join(',') + '\n';
        }
        
        // Add data rows
        data.forEach(row => {
          const values = headers.length > 0 
            ? headers.map(header => {
                const key = header.toLowerCase().replace(/\s+/g, '_');
                return row[key] || '';
              })
            : Object.values(row);
          
          csvContent += values.join(',') + '\n';
        });
      }
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${filename}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      toast.success('Arquivo CSV exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar CSV:', error);
      toast.error('Erro ao exportar arquivo CSV');
    } finally {
      setExporting(false);
    }
  };

  return {
    exportToExcel,
    exportToExcelMultiSheet,
    exportToPDF,
    exportToCSV,
    exporting
  };
};